import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import axiosClient from "../../api/axiosClient";
import styled from "styled-components";
import InputText from "../../components/Input/InputText";
import ChevronDownIcon from "../../components/Icons/ChevronDownIcon";

import Typography from "../../components/Typography";
import CardToogle from "../../components/Toogle/CardToogle";
import Button from "../../components/Button/Button";
import { theme } from "../../theme";
import {
	EVENT_TYPES,
	TERRAIN_TYPES,
	EXPERIENCE_LEVELS,
} from "../../constants/eventTypes";

const EditInterests = () => {
	const { t } = useTranslation("completeProfile");
	const { t: tUserProfile } = useTranslation("userProfile");
	const navigate = useNavigate();
	const location = useLocation();
	const { user, refreshUserData } = useAuth();
	const formRef = useRef(null);
	const [collapsed, setCollapsed] = useState({
		eventTypes: false,
		terrains: false,
		vehicleTypes: false,
		experiences: false,
		attendance: false,
		organizing: false,
		professional: false,
		suggestions: false,
	});

	const toggleSection = (key) => {
		setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
	};

	const [interests, setInterests] = useState({
		eventTypes: [],
		terrains: [],
		experiences: [],
		vehicleTypes: [],
		eventFrequency: "",
		organizingFrequency: "",
		professionalOrganizer: "",
		eventSuggestions: false,
		nearbyCity: "",
	});

	const [isLoading, setIsLoading] = useState(false);

	// Cargar datos del usuario al montar el componente
	useEffect(() => {
		if (user) {
			const newInterests = {
				eventTypes: user.eventTypeInterests || [],
				terrains: user.terrainInterests || [],
				experiences: user.experienceInterests || [],
				vehicleTypes: user.vehicleTypeInterests || [],
				eventFrequency: user.eventFrequency || "",
				organizingFrequency: user.organizingFrequency || "",
				professionalOrganizer: user.professionalOrganizer || "",
				eventSuggestions:
					user.receiveEventSuggestions !== undefined
						? user.receiveEventSuggestions
						: false,
				nearbyCity: user.nearbyCity || "",
			};
			setInterests(newInterests);
		}
	}, [user]);

	const handleToggleEventType = (eventTypeId) => {
		setInterests((prev) => ({
			...prev,
			eventTypes: prev.eventTypes.includes(eventTypeId)
				? prev.eventTypes.filter((type) => type !== eventTypeId)
				: [...prev.eventTypes, eventTypeId],
		}));
	};

	const handleToggleTerrain = (terrainId) => {
		setInterests((prev) => ({
			...prev,
			terrains: prev.terrains.includes(terrainId)
				? prev.terrains.filter((t) => t !== terrainId)
				: [...prev.terrains, terrainId],
		}));
	};

	const handleToggleExperience = (experienceId) => {
		setInterests((prev) => ({
			...prev,
			experiences: prev.experiences.includes(experienceId)
				? prev.experiences.filter((exp) => exp !== experienceId)
				: [...prev.experiences, experienceId],
		}));
	};

	const handleToggleVehicleType = (vehicleTypeId) => {
		setInterests((prev) => ({
			...prev,
			vehicleTypes: prev.vehicleTypes.includes(vehicleTypeId)
				? prev.vehicleTypes.filter((type) => type !== vehicleTypeId)
				: [...prev.vehicleTypes, vehicleTypeId],
		}));
	};

	const handleToggleFrequency = (frequencyId) => {
		setInterests((prev) => ({
			...prev,
			eventFrequency: frequencyId,
		}));
	};

	const handleToggleOrganizingFrequency = (frequencyId) => {
		setInterests((prev) => ({
			...prev,
			organizingFrequency: frequencyId,
		}));
	};

	const handleToggleProfessionalOrganizer = (value) => {
		setInterests((prev) => ({
			...prev,
			professionalOrganizer: value,
		}));
	};

	const handleToggleEventSuggestions = (value) => {
		setInterests((prev) => ({
			...prev,
			eventSuggestions: value,
		}));
	};

	const navigateBackToSettings = () => {
		if (location.state?.fromSettings) {
			navigate(-1);
			return;
		}

		if (location.state?.returnTo) {
			navigate(
				location.state.returnTo,
				location.state.returnToState
					? { state: location.state.returnToState }
					: undefined,
			);
			return;
		}

		navigate(-1);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		setIsLoading(true);

		try {
			if (
				interests.eventSuggestions &&
				(!interests.nearbyCity || !interests.nearbyCity.trim())
			) {
				toast.error(t("interests.nearbyCityRequired"));
				setIsLoading(false);
				return;
			}
			const dataToSend = {
				eventTypeInterests: JSON.stringify(interests.eventTypes), // Convert to JSON string
				terrainInterests: JSON.stringify(interests.terrains), // Convert to JSON string
				experienceInterests: JSON.stringify(interests.experiences), // Convert to JSON string
				vehicleTypeInterests: JSON.stringify(interests.vehicleTypes), // Convert to JSON string
				eventFrequency: interests.eventFrequency,
				organizingFrequency: interests.organizingFrequency,
				professionalOrganizer: interests.professionalOrganizer,
				receiveEventSuggestions: interests.eventSuggestions,
				nearbyCity: interests.eventSuggestions ? interests.nearbyCity : "",
			};

			const response = await axiosClient.put("/api/users/profile", dataToSend);

			if (response.data.success) {
				toast.success(t("interests.messages.updated"));
				await refreshUserData();
				navigateBackToSettings();
			}
		} catch (error) {
			console.error("Error updating interests:", error);
			toast.error(t("interests.messages.error"));
		} finally {
			setIsLoading(false);
		}
	};

	// Datos de los intereses con traducciones
	const eventTypes = EVENT_TYPES.map((type) => ({
		id: type,
		name: t(`interests.eventTypes.${type}`),
	}));

	const terrains = TERRAIN_TYPES.map((terrain) => ({
		id: terrain,
		name: t(`interests.terrains.${terrain}`),
	}));

	const experiences = EXPERIENCE_LEVELS.map((level) => ({
		id: level,
		name: t(`interests.experiences.${level}`),
	}));

	const vehicleTypes = [
		{ id: "sport-bike", name: t("interests.vehicleTypes.sport-bike") },
		{ id: "naked-bike", name: t("interests.vehicleTypes.naked-bike") },
		{ id: "scooter", name: t("interests.vehicleTypes.scooter") },
		{ id: "custom-bike", name: t("interests.vehicleTypes.custom-bike") },
		{
			id: "dual-sport-bike",
			name: t("interests.vehicleTypes.dual-sport-bike"),
		},
		{ id: "enduro-bike", name: t("interests.vehicleTypes.enduro-bike") },
		{ id: "trial-bike", name: t("interests.vehicleTypes.trial-bike") },
		{ id: "touring-bike", name: t("interests.vehicleTypes.touring-bike") },
		{ id: "electric-bike", name: t("interests.vehicleTypes.electric-bike") },
		{ id: "mini-bikes", name: t("interests.vehicleTypes.mini-bikes") },
		{ id: "pit-bikes", name: t("interests.vehicleTypes.pit-bikes") },
		{ id: "motocross-bike", name: t("interests.vehicleTypes.motocross-bike") },
		{
			id: "motorized-tricycle",
			name: t("interests.vehicleTypes.motorized-tricycle"),
		},
		{ id: "trike", name: t("interests.vehicleTypes.trike") },
		{ id: "sidecar-bike", name: t("interests.vehicleTypes.sidecar-bike") },
		{ id: "compact-car", name: t("interests.vehicleTypes.compact-car") },
		{ id: "sedan", name: t("interests.vehicleTypes.sedan") },
		{ id: "coupe", name: t("interests.vehicleTypes.coupe") },
		{ id: "convertible", name: t("interests.vehicleTypes.convertible") },
		{ id: "suv", name: t("interests.vehicleTypes.suv") },
		{ id: "crossover", name: t("interests.vehicleTypes.crossover") },
		{ id: "pickup-truck", name: t("interests.vehicleTypes.pickup-truck") },
		{
			id: "off-road-vehicle",
			name: t("interests.vehicleTypes.off-road-vehicle"),
		},
		{ id: "buggy", name: t("interests.vehicleTypes.buggy") },
		{ id: "atv", name: t("interests.vehicleTypes.atv") },
		{ id: "4x4", name: t("interests.vehicleTypes.4x4") },
		{ id: "go-kart", name: t("interests.vehicleTypes.go-kart") },
	];

	const frequencies = [
		{ id: "weekly", name: t("interests.attendanceFrequencies.weekly") },
		{ id: "monthly", name: t("interests.attendanceFrequencies.monthly") },
		{
			id: "special-getaways",
			name: t("interests.attendanceFrequencies.special-getaways"),
		},
		{
			id: "when-interesting",
			name: t("interests.attendanceFrequencies.when-interesting"),
		},
	];

	const organizingFrequencies = [
		{ id: "weekly", name: t("interests.organizingFrequencies.weekly") },
		{ id: "monthly", name: t("interests.organizingFrequencies.monthly") },
		{
			id: "special-getaways",
			name: t("interests.organizingFrequencies.special-getaways"),
		},
		{
			id: "when-interesting",
			name: t("interests.organizingFrequencies.when-interesting"),
		},
	];

	const professionalOrganizerOptions = [
		{ id: "yes", name: t("interests.professionalOrganizer.yes") },
		{ id: "no", name: t("interests.professionalOrganizer.no") },
		{ id: "not-sure", name: t("interests.professionalOrganizer.notSure") },
	];

	const eventSuggestionsOptions = [
		{ id: true, name: t("interests.eventSuggestions.yes") },
		{ id: false, name: t("interests.eventSuggestions.no") },
	];

	return (
		<>
			<Navigation>
				<NavbarContainer>
					<Typography
						as="h3"
						$variant="title-5-semibold"
						$variantTablet="body-1-semibold"
						$variantMobile="body-1-semibold"
						color={theme.colors.defaultStrong}
					>
						{tUserProfile("editInterests.title")}
					</Typography>
					<ActionsBlocks>
						<Button $variant="outline" onClick={navigateBackToSettings}>
							{tUserProfile("editInterests.cancel")}
						</Button>
						<Button
							type="button"
							disabled={isLoading}
							onClick={() => {
								if (formRef.current) {
									formRef.current.requestSubmit();
								}
							}}
						>
							{isLoading
								? tUserProfile("editInterests.saving")
								: tUserProfile("editInterests.save")}
						</Button>
					</ActionsBlocks>
				</NavbarContainer>
			</Navigation>
			<EditInterestContainer>
				<FormContainer>
					<IntroSection>
						{/* Introducción */}
						<Typography
							as="h3"
							$variant="title-5-semibold"
							color={theme.colors.defaultStrong}
						>
							{t("interests.title")}
						</Typography>
						<Typography
							as="p"
							$variant="body-1-regular"
							color={theme.colors.defaultWeak}
						>
							{t("interests.subtitle")}
						</Typography>
					</IntroSection>
					<InterestsContainer>
						<Form ref={formRef} onSubmit={handleSubmit}>
							<InnerForm>
								{/* ¿Qué tipo de plan te emociona más? */}
								<InterestSection>
									<CollapsibleHeader
										onClick={() => toggleSection("eventTypes")}
									>
										<SectionTitle>
											<Typography
												as="h3"
												$variant="title-5-semibold"
												color={theme.colors.defaultStrong}
											>
												{t("interests.eventTypes.title")}
											</Typography>
										</SectionTitle>
										<ChevronIcon
											$open={!collapsed.eventTypes}
											fill={theme.colors.defaultStrong}
											width={16}
											height={16}
										/>
									</CollapsibleHeader>
									{!collapsed.eventTypes && (
										<Typography
											$variant="body-2-regular"
											color={theme.colors.defaultWeak}
										>
											{t("interests.eventTypes.subtitle")}
										</Typography>
									)}
									{!collapsed.eventTypes && (
										<CardsGrid>
											{eventTypes.map((eventType) => (
												<CardToogle
													key={eventType.id}
													isActive={interests.eventTypes.includes(eventType.id)}
													onClick={() => handleToggleEventType(eventType.id)}
													category={eventType.name}
													textAlign="center"
												/>
											))}
										</CardsGrid>
									)}
								</InterestSection>

								{/* ¿En qué terrenos te gusta rodar? */}
								<InterestSection>
									<CollapsibleHeader onClick={() => toggleSection("terrains")}>
										<SectionTitle>
											<Typography
												as="h3"
												$variant="title-5-semibold"
												color={theme.colors.defaultStrong}
											>
												{t("interests.terrains.title")}
											</Typography>
										</SectionTitle>
										<ChevronIcon
											$open={!collapsed.terrains}
											fill={theme.colors.defaultStrong}
											width={16}
											height={16}
										/>
									</CollapsibleHeader>
									{!collapsed.terrains && (
										<Typography
											$variant="body-2-regular"
											color={theme.colors.defaultWeak}
										>
											{t("interests.terrains.subtitle")}
										</Typography>
									)}
									{!collapsed.terrains && (
										<CardsGrid>
											{terrains.map((terrain) => (
												<CardToogle
													key={terrain.id}
													isActive={interests.terrains.includes(terrain.id)}
													onClick={() => handleToggleTerrain(terrain.id)}
													category={terrain.name}
													textAlign="center"
												/>
											))}
										</CardsGrid>
									)}
								</InterestSection>

								{/* ¿Qué tipos de vehículos te interesan? */}
								<InterestSection>
									<CollapsibleHeader
										onClick={() => toggleSection("vehicleTypes")}
									>
										<SectionTitle>
											<Typography
												as="h3"
												$variant="title-5-semibold"
												color={theme.colors.defaultStrong}
											>
												{t("interests.vehicleTypes.title")}
											</Typography>
										</SectionTitle>
										<ChevronIcon
											$open={!collapsed.vehicleTypes}
											fill={theme.colors.defaultStrong}
											width={16}
											height={16}
										/>
									</CollapsibleHeader>
									{!collapsed.vehicleTypes && (
										<Typography
											$variant="body-2-regular"
											color={theme.colors.defaultWeak}
										>
											{t("interests.vehicleTypes.subtitle")}
										</Typography>
									)}
									{!collapsed.vehicleTypes && (
										<CardsGrid>
											{vehicleTypes.map((vehicleType) => (
												<CardToogle
													key={vehicleType.id}
													isActive={interests.vehicleTypes.includes(
														vehicleType.id,
													)}
													onClick={() =>
														handleToggleVehicleType(vehicleType.id)
													}
													category={vehicleType.name}
													textAlign="center"
												/>
											))}
										</CardsGrid>
									)}
								</InterestSection>

								{/* ¿Qué nivel de experiencia tienes? */}
								<InterestSection>
									<CollapsibleHeader
										onClick={() => toggleSection("experiences")}
									>
										<SectionTitle>
											<Typography
												as="h3"
												$variant="title-5-semibold"
												color={theme.colors.defaultStrong}
											>
												{t("interests.experiences.title")}
											</Typography>
										</SectionTitle>
										<ChevronIcon
											$open={!collapsed.experiences}
											fill={theme.colors.defaultStrong}
											width={16}
											height={16}
										/>
									</CollapsibleHeader>
									{!collapsed.experiences && (
										<Typography
											$variant="body-2-regular"
											color={theme.colors.defaultWeak}
										>
											{t("interests.experiences.subtitle")}
										</Typography>
									)}
									{!collapsed.experiences && (
										<CardsGrid>
											{experiences.map((experience) => (
												<CardToogle
													key={experience.id}
													isActive={interests.experiences.includes(
														experience.id,
													)}
													onClick={() => handleToggleExperience(experience.id)}
													category={experience.name}
													textAlign="center"
												/>
											))}
										</CardsGrid>
									)}
								</InterestSection>

								{/* ¿Con qué frecuencia te gustaría participar? */}
								<InterestSection>
									<CollapsibleHeader
										onClick={() => toggleSection("attendance")}
									>
										<SectionTitle>
											<Typography
												as="h3"
												$variant="title-5-semibold"
												color={theme.colors.defaultStrong}
											>
												{t("interests.attendanceFrequencies.title")}
											</Typography>
										</SectionTitle>
										<ChevronIcon
											$open={!collapsed.attendance}
											fill={theme.colors.defaultStrong}
											width={16}
											height={16}
										/>
									</CollapsibleHeader>
									{!collapsed.attendance && (
										<Typography
											$variant="body-2-regular"
											color={theme.colors.defaultWeak}
										>
											{t("interests.attendanceFrequencies.subtitle")}
										</Typography>
									)}
									{!collapsed.attendance && (
										<FrequencyGrid>
											{frequencies.map((frequency) => (
												<CardToogle
													key={frequency.id}
													isActive={interests.eventFrequency === frequency.id}
													onClick={() => handleToggleFrequency(frequency.id)}
													category={frequency.name}
													textAlign="center"
												/>
											))}
										</FrequencyGrid>
									)}
								</InterestSection>

								{/* ¿Con qué frecuencia te gustaría organizar eventos o actividades? */}
								<InterestSection>
									<CollapsibleHeader
										onClick={() => toggleSection("organizing")}
									>
										<SectionTitle>
											<Typography
												as="h3"
												$variant="title-5-semibold"
												color={theme.colors.defaultStrong}
											>
												{t("interests.organizingFrequencies.title")}
											</Typography>
										</SectionTitle>
										<ChevronIcon
											$open={!collapsed.organizing}
											fill={theme.colors.defaultStrong}
											width={16}
											height={16}
										/>
									</CollapsibleHeader>
									{!collapsed.organizing && (
										<Typography
											$variant="body-2-regular"
											color={theme.colors.defaultWeak}
										>
											{t("interests.organizingFrequencies.subtitle")}
										</Typography>
									)}
									{!collapsed.organizing && (
										<FrequencyGrid>
											{organizingFrequencies.map((frequency) => (
												<CardToogle
													key={frequency.id}
													isActive={
														interests.organizingFrequency === frequency.id
													}
													onClick={() =>
														handleToggleOrganizingFrequency(frequency.id)
													}
													category={frequency.name}
													textAlign="center"
												/>
											))}
										</FrequencyGrid>
									)}
								</InterestSection>

								{/* ¿Te interesa organizar eventos de forma profesional? */}
								<InterestSection>
									<CollapsibleHeader
										onClick={() => toggleSection("professional")}
									>
										<SectionTitle>
											<Typography
												as="h3"
												$variant="title-5-semibold"
												color={theme.colors.defaultStrong}
											>
												{t("interests.professionalOrganizer.title")}
											</Typography>
										</SectionTitle>
										<ChevronIcon
											$open={!collapsed.professional}
											fill={theme.colors.defaultStrong}
											width={16}
											height={16}
										/>
									</CollapsibleHeader>
									{!collapsed.professional && (
										<CardsGrid>
											{professionalOrganizerOptions.map((option) => (
												<CardToogle
													key={option.id}
													isActive={
														interests.professionalOrganizer === option.id
													}
													onClick={() =>
														handleToggleProfessionalOrganizer(option.id)
													}
													category={option.name}
													textAlign="center"
												/>
											))}
										</CardsGrid>
									)}
								</InterestSection>

								{/* ¿Te gustaría recibir sugerencias de eventos? */}
								<InterestSection>
									<CollapsibleHeader
										onClick={() => toggleSection("suggestions")}
									>
										<SectionTitle>
											<Typography
												as="h3"
												$variant="title-5-semibold"
												color={theme.colors.defaultStrong}
											>
												{t("interests.eventSuggestions.title")}
											</Typography>
										</SectionTitle>
										<ChevronIcon
											$open={!collapsed.suggestions}
											fill={theme.colors.defaultStrong}
											width={16}
											height={16}
										/>
									</CollapsibleHeader>
									{!collapsed.suggestions && (
										<Typography
											$variant="body-2-regular"
											color={theme.colors.defaultWeak}
										>
											{t("interests.eventSuggestions.subtitle")}
										</Typography>
									)}
									{!collapsed.suggestions && (
										<CardsGrid>
											{eventSuggestionsOptions.map((option) => (
												<CardToogle
													key={option.id.toString()}
													isActive={interests.eventSuggestions === option.id}
													onClick={() =>
														handleToggleEventSuggestions(option.id)
													}
													category={option.name}
													textAlign="center"
												/>
											))}
										</CardsGrid>
									)}
									{!collapsed.suggestions && interests.eventSuggestions && (
										<NearbyCityWrapper>
											<Typography
												as="label"
												$variant="body-2-medium"
												color={theme.colors.defaultStrong}
											>
												{t("interests.nearbyCity.label")}
											</Typography>
											<InputText
												type="text"
												name="nearbyCity"
												value={interests.nearbyCity || ""}
												onChange={(e) =>
													setInterests((prev) => ({
														...prev,
														nearbyCity: e.target.value,
													}))
												}
												$size=""
												placeholder={t("interests.nearbyCity.placeholder")}
											/>
										</NearbyCityWrapper>
									)}
								</InterestSection>
							</InnerForm>

							<Actions>
								<Button
									type="button"
									$variant="outline"
									onClick={() => navigate(-1)}
								>
									{tUserProfile("editInterests.cancel")}
								</Button>
								<Button type="submit" disabled={isLoading}>
									{isLoading
										? tUserProfile("editInterests.saving")
										: tUserProfile("editInterests.save")}
								</Button>
							</Actions>
						</Form>
					</InterestsContainer>
				</FormContainer>
			</EditInterestContainer>
		</>
	);
};

// Styled Components

const Navigation = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.fill.defaultMain};
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  z-index: 1;
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultWeak};
`;

const NavbarContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.md};
  max-width: 1400px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
  }
`;

const ActionsBlocks = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const EditInterestContainer = styled.section`
  // max-width: 800px;
  // margin: 0 auto;
  // padding: 2rem;
  // min-height: 100vh;
  // background-color: ${({ theme }) => theme.colors.background};

  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: ${({ theme }) => theme.fill.defaultSubtle};
  padding-top: 100px;
  padding-bottom: 120px;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding-top: 0px;
    padding-bottom: 0px;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 600px;
  border-radius: ${({ theme }) => theme.sizing.sm};
  background: ${({ theme }) => theme.fill.defaultMain};
  box-shadow: 0px 2px 12px 0px rgba(26, 26, 26, 0.04);
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    border-radius: 0px;
    box-shadow: none;
    border: none;
    padding-top: 80px;
  }
`;

const InterestsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const IntroSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  text-align: center;
  padding: ${({ theme }) => theme.sizing.md};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultWeak};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const InnerForm = styled.div`
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.md};
  padding: ${({ theme }) => theme.sizing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
  }
`;

const InterestSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  width: 100%;
  padding-bottom: ${({ theme }) => theme.sizing.md};
  border-bottom: 1px solid #dcdcdc;

  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${({ theme }) => theme.sizing.xs};
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FrequencyGrid = styled(CardsGrid)`
  grid-template-columns: repeat(2, 1fr);
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizing.xs};
  justify-content: space-between;
  border-top: 1px solid ${({ theme }) => theme.border.defaultWeak};
  padding: ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
  }
`;

const NearbyCityWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  margin-top: ${({ theme }) => theme.sizing.xs};
`;

const CollapsibleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const ChevronIcon = styled(ChevronDownIcon)`
  transition: transform 0.2s ease;
  transform: ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0deg)")};
`;

export default EditInterests;
